import { PartyService } from 'src/app/services/api/party/party.service';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {  Subscription } from 'rxjs';
import {  map, pluck} from 'rxjs/operators';
import { LoaderService } from 'src/app/core/header-menu/loader/loader.service';
import { Product } from 'src/app/models/interface/product/product';
import { generateExternalID, ProductSpecId } from 'src/app/models/order/order.model';
import { FtthService } from 'src/app/services/api/ftth/ftth.service';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { TemplatesService } from 'src/app/services/api/templates/templates.service';
import { ProductDepthLevelCfsActionsComponent } from '../product-depth-level-cfs-actions.component';


export class DescribedBy {
  externalId?: string;

  constructor(
    public value: string,
    public specId: ProductSpecId,
  ) {
  }
}

export class ChangeRequest {
  products: {
    name: string;
    domainName: 'FTTH';
    searchKey: string;
    template: string;
    describedByList: DescribedBy[];
    specId: string;
  }[];

  productOrder: {
    domain: 'FTTH';
    productOrderType: string;
    externalId: string;
    crm: string;
    action: string;
    orderDate: string;
    completionDate?: string;
    project: string;
    requestStartDate: string;
    requestCompletionDate?: string;
    expectedStartDate?: string;
    expectedCompletionDate?: string;
    execution: number;
    urgent: boolean;
    category: string;
    productRef: { relatedId: string }[];
  };

  constructor(product: Product, template: string, tvRemoteId?: string) {
    const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
    const now = moment();
    const externalId = generateExternalID();

    let homeId;
    const describedByList: DescribedBy[] = [];

    describedByList.push(new DescribedBy(template, ProductSpecId.pc_ftth_template));

    for (const charValue of product.productCharValues) {
      switch (charValue.specId as ProductSpecId) {
        case ProductSpecId.pc_ftth_home_id:
          homeId = charValue.value;
          break;
        case ProductSpecId.pc_ftth_territory_user:
          describedByList.push(new DescribedBy(charValue.value, ProductSpecId.pc_ftth_territory_user));
          break;
      }
    }

    if (tvRemoteId) {
      describedByList.push(new DescribedBy(tvRemoteId, ProductSpecId.pc_ftth_remote_id_tv));
    }

    this.products = [{
      name: product.name,
      domainName: 'FTTH',
      searchKey: homeId,
      template,
      specId: product.specId,
      describedByList,
    }];

    this.productOrder = {
      domain: 'FTTH',
      productOrderType: product.specId.includes('Business') ? '2' : '1',
      externalId,
      crm: 'customer-portal',
      action: 'update_rfs',
      orderDate: now.format(dateFormat),
      project: `ONIVIA-${externalId}-MOD`,
      requestStartDate: now.format(dateFormat),
      execution: 0,
      urgent: false,
      category: 'Product',
      productRef: [{ relatedId: '1' }],
    }
  }
}


@Component({
  selector: 'app-change-service',
  templateUrl: './change-service.component.html',
  styleUrls: ['./change-service.component.scss']
})
export class ChangeServiceComponent implements OnInit {

  changeForm: FormGroup;
  loading: boolean;
  templates: any[];
  inoId: string;

  private segment: 'EMP' | 'RES';
  private templateName: string;
  private item: Product;
  private subscriptions = new Subscription();
  isWholesaleCorp: string;

  constructor(
    private ftthService: FtthService,
    private templatesService: TemplatesService,
    private loaderService: LoaderService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ProductDepthLevelCfsActionsComponent>,
    private partyService: PartyService,
    @Inject(MAT_DIALOG_DATA) private data: {
      item: Product;
      type: string;
    },
  ) {
  }

  ngOnInit() {
    this.item = this.data.item;

    this.subscriptions.add(this.loaderService.loading$.subscribe(loading => this.loading = loading));

    this.initForm();
    this.initFormValues();

    this.partyService.getCustomers().subscribe(
      (data) => {
        this.initTemplates(data.find(isp => isp.namingId === this.item.origin).namingId);
      },
      (error) => {
        this.dialogRef.close(false);
        this.notificationsService.showNotification(this.translateService.instant('CHANGE_SERVICE.ispList'), NotificationLevel.ERROR);
      }
    )

  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private initForm() {
    this.changeForm = this.formBuilder.group({
      template: this.formBuilder.control('', Validators.required),
    });
  }

  private initFormValues() {
    for (const cfsCharValue of this.data.item.productCharValues) {
      switch (cfsCharValue.specId) {
        case 'pc_ftth_client_segment':
          //this.segment = cfsCharValue.value as 'EMP' | 'RES';
          this.segment = cfsCharValue.value as 'RES';
          break;
        case 'pc_ftth_template':
          this.templateName = cfsCharValue.value;
          break;
      }
    }
  }

  private initTemplates(ispName: string) {
    this.isWholesaleCorp = this.item.specId.includes('Wholesale_Corporate') ? "true" : "false";
    this.subscriptions.add(
      this.templatesService.getTemplates(null, '50', null, {/*segment,*/ isp: ispName }, this.isWholesaleCorp)
        .pipe(
          pluck('content'),
          map((templates) => {

            //this.inoId = Object.keys(inoIdsResponse.inoIds)[0];
            const mappings = [];

            for (const template of templates) {
              if (!template.isActive) {
                continue;
              }

              for (const mapping of template.mappings) {
                if (
                  mapping.externalTemplateISP !== this.templateName
                  && mapping.territoryUser === ispName
                ) {
                  mappings.push(mapping);
                  break;
                }
              }
            }

            return mappings;

          }
          ),
        )
        .subscribe(templates => this.templates = templates));

  }

  templateChange() {
    if (this.templateIsTv()) {
      this.changeForm.addControl('tvremoteid', this.formBuilder.control('', Validators.required));
    } else {
      this.changeForm.removeControl('tvremoteid');
    }
  }

  templateIsTv(): boolean {
    return this.templateControl.value ? (this.templateControl.value.externalTemplateISP.includes('TV') || this.templateControl.value.externalTemplateISP.includes('tv')) : false;
  }

  submit() {
    if (this.changeForm.valid && !this.loading) {
      this.ftthService.changeService({
        'operador': this.item.origin,
        'idadministrativo': this.item.productCharValues.find(charValue => charValue.specId === "pc_ftth_service_id").value,
        'template': this.templateControl.value.externalTemplateISP,
        'tvremoteid': this.templateIsTv() ? this.tvRemoteIdControl.value : undefined
      })
        .subscribe(
          (data) => {
            this.loading = false;
            if (data.resultado === 'OK') {
              this.dialogRef.close(true);
              this.notificationsService.showNotification(this.translateService.instant('CHANGE_SERVICE.success'), NotificationLevel.SUCCESS);
            }
            else {
              this.notificationsService.showNotification("ERROR (" + data.codResultado + "): " + data.descResultado, NotificationLevel.ERROR);
            }
          },
          error => {
            this.loading = false;
            this.notificationsService.showNotification("ERROR (" + error.data.codResultado + "): " + error.data.descResultado, NotificationLevel.ERROR);
          });
    }
  }

  get templateControl(): FormControl {
    return this.changeForm.get('template') as FormControl;
  }

  get tvRemoteIdControl(): FormControl {
    return this.changeForm.get('tvremoteid') as FormControl;
  }

}
